import { useState } from 'react';
import { X, Building2 } from 'lucide-react';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import { Button } from './ui';
import { Portal } from './Portal';
import type { ClientInsert } from '../types/client';
import { log } from '../lib/logger';

interface QuickClientModalProps {
  initialName: string;
  onClose: () => void;
  onCreated: (clientName: string) => void;
}

export function QuickClientModal({ initialName, onClose, onCreated }: QuickClientModalProps) {
  const { user } = useAuth();
  const { toast } = useModal();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: initialName,
    vat_number: '',
    address: '',
    contact_info: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setLoading(true);
    try {
      const existing = await clientService.getByName(formData.name);
      if (existing) {
        toast.warning('Já existe um cliente com este nome');
        setLoading(false);
        return;
      }

      const clientData: ClientInsert = {
        name: formData.name.trim(),
        vat_number: formData.vat_number || null,
        address: formData.address || null,
        contact_info: formData.contact_info || null,
        user_id: user?.id,
      };

      await clientService.create(clientData);
      toast.success(`Cliente "${formData.name}" criado`);
      onCreated(formData.name.trim());
    } catch (err) {
      log.error('Error creating client:', err);
      toast.error('Não foi possível criar o cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100-2xl w-full max-w-lg animate-in fade-in duration-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Building2 className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Novo cliente</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                autoFocus
                placeholder="Nome do cliente ou empresa"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                NIF
              </label>
              <input
                type="text"
                value={formData.vat_number}
                onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                placeholder="ex: PT123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Morada
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Rua, código postal, cidade"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Contacto
              </label>
              <input
                type="text"
                value={formData.contact_info}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                placeholder="Email, telefone ou pessoa de contacto"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" onClick={onClose} variant="tertiary" size="sm">
                Cancelar
              </Button>
              <Button type="submit" disabled={loading || !formData.name.trim()} variant="primary" size="sm">
                {loading ? 'A criar…' : 'Criar cliente'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
